import { useMemo } from 'react';
import type { CreateProductFormData, UpdateProductFormData, FormProgress } from '../types/product-form.types';

interface UseFormProgressOptions<T> {
  formValues: T;
  imageCount: number;
  requirements?: {
    showGender?: boolean;
    showSizes?: boolean;
  };
  mode?: 'create' | 'edit';
}

export function useProductFormProgress<T extends CreateProductFormData | UpdateProductFormData>({
  formValues,
  imageCount,
  requirements = {},
  mode = 'create',
}: UseFormProgressOptions<T>): FormProgress {
  return useMemo(() => {
    const baseRequiredFields = mode === 'create'
      ? ['shopId', 'categoryId', 'name', 'description', 'price', 'quantity', 'currency', 'unit', 'weight', 'weightUnit']
      : ['categoryId', 'name', 'description', 'price', 'quantity', 'currency', 'unit', 'weight', 'weightUnit'];

    const requiredFields = [...baseRequiredFields];

    if (requirements.showGender) requiredFields.push('gender');

    let completed = requiredFields.filter((field) => {
      const value = formValues[field as keyof T];
      if (field === 'weight') return typeof value === 'number' && value > 0;
      return value !== '' && value !== undefined && value !== 0;
    }).length;

    // Add images to calculation
    if (imageCount > 0) completed++;
    const total = requiredFields.length + 1; // +1 for images

    return {
      completedFields: completed,
      totalFields: total,
      progress: (completed / total) * 100,
    };
  }, [formValues, imageCount, requirements, mode]);
}
